import { amazonCoverDimensions, kindleCoverDimensions } from '@/constants/coverDimensions.ts'
import type { TAmazonBindingType } from '@/types/amazon-cover.ts'
import type { TAmazonCoverUnitType } from '@/types/cover'
import type { TAmazonCoverSize } from '@/types/cover'
import type { TAmazonCover } from '@/types/amazon-cover'
import type { TDropdownOption } from '@/types/dropdown'

export default class PageSize {
  public width: number
  public height: number

  constructor(width: number, height: number) {
    this.width = width
    this.height = height
  }

  static getPageSizeAttribute(body: HTMLBodyElement) {
    return body.getAttribute('data-page-size')
  }

  static _getPageSizeFromAttr(sizeAttr: string) {
    if (!sizeAttr) {
      return null
    }
    const parts = sizeAttr.split('x')
    if (parts.length !== 2) {
      return null
    }
    const bodyWidth = parseInt(parts[0]!)
    const bodyHeight = parseInt(parts[1]!)
    if (
      !bodyWidth ||
      !bodyHeight ||
      bodyWidth <= 0 ||
      bodyHeight <= 0 ||
      !Number.isInteger(bodyWidth) ||
      !Number.isInteger(bodyHeight)
    ) {
      return null
    }
    return new PageSize(bodyWidth, bodyHeight)
  }

  static getPageSizeFromBodyString(bodyStr: string) {
    const line = bodyStr.split('\n')[0]?.replace('<body', '<div')
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = line ?? ''
    const element = tempDiv.firstElementChild as HTMLElement // Get the first child (the div in this case)

    const dataSize = element.getAttribute('data-page-size')
    if (!dataSize) {
      return null
    }
    return PageSize._getPageSizeFromAttr(dataSize)
  }

  static getPageSizeFromBody(body: HTMLBodyElement) {
    const bodySize = PageSize.getPageSizeAttribute(body)
    if (!bodySize) {
      return PageSize.getPageSizeFromBodyString(body.outerHTML)
    }
    return PageSize._getPageSizeFromAttr(bodySize)
  }
}

export function getAmazonDimensionOptionsByUnit(
  unit: TAmazonCoverUnitType,
  filterType?: TAmazonBindingType,
  popular?: boolean,
) {
  return amazonCoverDimensions
    .filter((d) => {
      if (!filterType) return true
      if (filterType === 'paperback') return d.paperback
      if (filterType === 'hardcover') return d.hardcover
      if (popular) return d.popular
      return true
    })
    .map((d) => ({
      name: d.name,
      label: d.labels[unit],
    }))
}

export function findNearestAmazonCoverSize(widthMM: number, heightMM: number): TAmazonCoverSize {
  return amazonCoverDimensions.reduce((closest, current) => {
    const closestDistance = Math.hypot(closest.width - widthMM, closest.height - heightMM)
    const currentDistance = Math.hypot(current.width - widthMM, current.height - heightMM)
    return currentDistance < closestDistance ? current : closest
  })
}

export function getKindleSizeToDisplay(cover: TAmazonCover | null, sizeUnit: TDropdownOption | undefined): string {
  if (!cover) return ''

  if (!sizeUnit) {
    const front = cover.page_setup.front
    return `${Math.round(front.width)} × ${Math.round(front.height)} mm`
  }

  const unit = sizeUnit?.name === 'in' ? 'in' : 'mm'

  return kindleCoverDimensions.labels[unit]
}

export function getSizeToDisplay(
  cover: TAmazonCover | null,
  closestDimension: TAmazonCoverSize | null,
  sizeUnit: TDropdownOption | undefined,
): string {
  if (!cover) return ''

  const unit = sizeUnit?.name === 'in' ? 'in' : 'mm'

  if (!sizeUnit || !closestDimension || !closestDimension.labels[unit]) {
    const front = cover.page_setup.front
    return `${Math.round(front.width)} × ${Math.round(front.height)}mm`
  }

  return closestDimension.labels[unit]
}
